import "./UserCard.scss";
import { useState, useEffect } from "react";
import Loading from "../../utils/loading.jsx";
import service from "../../services/Service.js";
import { notification, Select, Checkbox, Space, Button, Tabs, Input } from "antd";
import { SaveOutlined, SafetyOutlined, KeyOutlined, ApiOutlined } from '@ant-design/icons';
import { withLocale } from '../../l10n/index.jsx';
import { getTokenInfo, hasPrivilege } from "../../api/token.js";
import withNavigation from '../hooks/withNavigation.jsx';
import ApiKeyList from "../ApiKeyList/ApiKeyList.jsx";
import { getTimezoneList } from '../../utils/dates';
import { USER_UUID, USER_TIMEZONE } from '../../utils/constants';
import { passwordStrengthValidation, emailValidation } from "../../utils/formValidation.jsx";
import { useGetTenantsLookupQuery, useGetRolesLookupQuery } from '../../services/lookupApi.js';

const { Option } = Select;
const UserCard = ({ lang, ident, navigate }) => {
  // States
  const [user, setUser] = useState({
    isNew: true,
    uuid: "",
    name: "",
    lastname: "",
    username: "",
    email: "",
    tenant_uuid: "",
    role_uuid: "",
    timezone: "",
    active: false,
    password: "",
    repeatPassword: "",
    });

  const [username, setUsername] = useState(null);
  const [errors, setErrors] = useState({ password: '', strength: '' });
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editPrivilege, setEditPrivilege] = useState(false);
  const [tenantName, setTenantName] = useState("");

  const { data: tenants = [] } = useGetTenantsLookupQuery();
  const { data: roles = [] } = useGetRolesLookupQuery(undefined, { skip: !hasPrivilege('users_edit') });
  
  // Effects
  useEffect(() => {
    const fetchData = async () => {
      const timezonesData = getTimezoneList();
      const editPriv = hasPrivilege('users_edit');

      setEditPrivilege(editPriv);
      setLoading(true);
      setTimezones(timezonesData);

      if (!ident) {
        setLoading(false);
        return;
      }

      await service.get("users/" + ident).then((res) => {
        if (res.data.ok === false) {
          notification["error"]({
            message: res.data.message,
          });
          setLoading(false);
          return;
        }
        if (!res.data.data) {
          setLoading(false);
          return;
        }

        const userData = {
          name: res.data.data[0].name,
          lastname: res.data.data[0].lastname,
          username: res.data.data[0].username,
          email: res.data.data[0].email,
          tenant_uuid: res.data.data[0].tenant_uuid,
          role_uuid: res.data.data[0].role_uuid,
          timezone: res.data.data[0].timezone,
          active: res.data.data[0].active,
          uuid: res.data.data[0].uuid,
          isNew: false,
          password: "",
          repeatPassword: ""
        }

        setUser(userData);
        setUsername(userData.username);
      });

      setLoading(false);
    };

    fetchData();

  }, [ident, lang]);

useEffect(() => {
    if (user.tenant_uuid) {
      const tenant = tenants.find(e => e.uuid === user.tenant_uuid);
      setTenantName(tenant ? tenant.name : lang.handle("Sin organización"));
    } else {
      setTenantName(lang.handle("Sin organización"));
    }
  }, [user.tenant_uuid, tenants, lang]);

  // Event Handlers
  const onInputChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    switch (name) {
      case 'password': {
        const newRepeatPasswordError =
          user.repeatPassword === "" || value === user.repeatPassword
            ? ""
            : lang.handle('¡Las contraseñas deben coincidir!');
        newErrors.password = newRepeatPasswordError;
        
        const strengthResult = passwordStrengthValidation(value);
        newErrors.strength = strengthResult.valid
          ? ''
          : lang.handle(strengthResult.message);
        break;
      }
      case 'repeatPassword': {
        const newPasswordError =
          user.password === value
            ? ""
            : lang.handle('¡Las contraseñas deben coincidir!');
        newErrors.password = newPasswordError;
        break;
      }
      default:
        break;
    }
    setUser({ ...user, [name]: value });
    setErrors(newErrors);
  }

  const onChangeSelect = (value, e) => {
    if(e.type === "tenant_name") {
      const index_tenant = tenants.find((item) => item.name === value).uuid;

      setUser({ ...user, tenant_uuid: index_tenant });
      setTenantName(value);

    } else {
      setUser({ ...user, [e.type]: value });
    }
  };

  const onChangeCheck = () => {
    setUser({ ...user, active: !user.active });
  };

  const onSaveData = () => {
    navigate("/users/list");
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    
    if (!user.username || user.username === "") {
      notification["error"]({
        message: lang.handle('El nombre de usuario es un campo obligatorio'),
      });
      return;
    }
    if (!user.email || user.email === "") {
      notification["error"]({
        message: lang.handle('El correo electrónico es un campo obligatorio'),
      });
      return;
    }
    // El onClick del botón cancela el envío del formulario, así que el type="email"
    // del campo no llega a validarse: se comprueba aquí.
    if (!emailValidation({ value: user.email })) {
      notification["error"]({
        message: lang.handle('El correo electrónico no es válido'),
      });
      return;
    }
    if (!user.tenant_uuid || user.tenant_uuid === "") {
      notification["error"]({
        message: lang.handle('La organización es un campo obligatorio'),
      });
      return;
    }
    if (editPrivilege && (!user.role_uuid || user.role_uuid === "")) {
      notification["error"]({
        message: lang.handle('El nivel es un campo obligatorio'),
      });
      return;
    }
    if (user.isNew && (user.password === "" || user.repeatPassword === "")) {
      notification["error"]({
        message: lang.handle('La contraseña es un campo obligatorio'),
      });
      return;
    }
    if (user.isNew) {
      const strengthResult = passwordStrengthValidation(user.password);
      if (!strengthResult.valid) {
        notification["error"]({
          message: lang.handle(strengthResult.message),
        });
        return;
      }
    }
    if (!errors.password) {
      if (user.uuid && !user.isNew) {
        await service
          .put("users/" + user.uuid, user)
          .then(async (res) => {
            if (res.data.ok === false) {
              notification["error"]({
                message: res.data.message,
              });
              return;
            } else {
              notification["success"]({
                message: lang.handle('Usuario guardado correctamente'),
              });
              if (user.uuid === localStorage.getItem(USER_UUID)) {
                localStorage.setItem(USER_TIMEZONE, user.timezone || '');
              }
              if ( editPrivilege ) {
                await service
                  .put("vpn/" + username, { username: user.username, tenant: user.tenant_uuid })
                  .then((res) => {
                    if (res.data.ok === false && res.data.message) {
                      notification["error"]({
                        message: lang.handle(res.data.message),
                      });
                      return;
                    } else {
                      notification["success"]({
                        message: lang.handle('VPN actualizada correctamente'),
                      });
                    }
                  })
                  .catch((err) => {
                    console.log("Error devuelto: " + err);
                  });
              }
              onSaveData();
            }
          })
          .catch((err) => {
            console.log("Error devuelto: " + err);
          });
      } else {
        await service
          .post("users/sign-up", user)
          .then((res) => {
            if (res.data.ok === false) {
              notification["error"]({
                message: res.data.message,
              });
              return;
            } else {
              notification["success"]({
                message: lang.handle('Usuario creado correctamente'),
              });
              onSaveData();
            }
          })
          .catch(() => {
            notification["error"]({
              message: lang.handle('Error al crear el usuario'),
            });
          });
      }
    } else {
      if (errors.password) {
        notification["error"]({
          message: lang.handle('Las contraseñas deben coincidir'),
        });
      }
    }
  };

  // Navigation handlers
  const changePassword = () => {
    setLoading(true);
    navigate(`/users/changepassword/${ident}`);
  }

  const changeCloudApiKey = () => {
    setLoading(true);
    navigate(`/users/cloudapikey/${ident}`);
  }

  const manageTwoFactor = () => {
    setLoading(true);
    navigate(`/users/security/${ident}`);
  };

  // Component render
  const uuid = getTokenInfo().uuid;

  const title = user.isNew
    ? lang.handle('Nuevo usuario')
    : lang.handle('Editando usuario') + ": " + user.username;

  const userTab = {
      label: lang.handle('Editar'), key: '1', children: <div>
        <div className="form-group row">
          {loading && <Loading loading={loading}/>}
          <form onSubmit={onSubmit} className="p-3">
            <h6 className="user-section-title">
              {lang.handle('Datos personales')}
            </h6>
            <div className="form-group row">
              <label
                htmlFor="name"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle('Nombre')}{" "}
              </label>
              <div className="col-sm-10">
                <input
                  className="form-control"
                  type="name"
                  id="name"
                  name="name"
                  value={user.name}
                  onChange={onInputChange}
                />
              </div>
            </div>
            <div className="form-group row">
              <label
                htmlFor="lastname"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle('Apellidos')}{" "}
              </label>
              <div className="col-sm-10">
                <input
                  type="lastname"
                  name="lastname"
                  className="form-control"
                  id="lastname"
                  value={user.lastname}
                  onChange={onInputChange}
                />
              </div>
            </div>
            <div className="form-group row">
              <label
                htmlFor="username"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle("Nombre de usuario")}{" "}
              </label>
              <div className="col-sm-10">
                <input
                  type="username"
                  name="username"
                  className="form-control"
                  id="username"
                  value={user.username}
                  onChange={onInputChange}
                  required
                  disabled={!editPrivilege}
                />
              </div>
            </div>
            <div className="form-group row">
              <label
                htmlFor="email"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle('Correo electrónico')}{" "}
              </label>
              <div className="col-sm-10">
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  id="email"
                  value={user.email}
                  onChange={onInputChange}
                  required
                />
              </div>
            </div>
            {user.isNew && <div className="form-group row">
              <label
                htmlFor="password"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle('Contraseña')}{" "}
              </label>
              <div className="col-sm-10">
                <Input.Password
                  className="form-control"
                  id="password"
                  onChange={onInputChange}
                  name="password"
                  value={user.password}
                />
                {errors.strength.length > 0 && <span className='boardFormError'>{errors.strength}</span>}
              </div>
            </div>}
            {user.isNew && <div className="form-group row">
              <label
                htmlFor="repeatPassword"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle('Repetir contraseña')}{" "}
              </label>
              <div className="col-sm-10">
                <Input.Password
                  className="form-control"
                  id="repeatPassword"
                  onChange={onInputChange}
                  name="repeatPassword"
                  value={user.repeatPassword}
                />
                {errors.password.length > 0 && <span className='boardFormError'>{errors.password}</span>}
              </div>
            </div>}
            <div className="form-group row">
              <label
                htmlFor="tenant_uuid"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle('Organización')}{" "}
              </label>
              <div className="col-sm-10">
                <Select
                  showSearch
                  style={{ width: 200 }}
                  optionFilterProp="tenant_uuid"
                  onSelect={(value, event) => onChangeSelect(value, event)}
                  name="tenant_uuid"
                  value={tenantName}
                  className="register-form__select form-select-input form-control"
                  id="tenantSelect"
                  filterOption={(input, option) =>
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  disabled={!editPrivilege}
                >
                  {tenants.map((tenant) => {
                    return (
                      <Option key={tenant.uuid} type="tenant_name" value={tenant.name}>{tenant.name}</Option>
                    )
                  })}
                </Select>
              </div>
            </div>
            {editPrivilege && <div className="form-group row">
              <label
                htmlFor="role_uuid"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {lang.handle('Nivel')}{" "}
              </label>
              <div className="col-sm-10">
                <Select
                  showSearch
                  style={{ width: 200 }}
                  placeholder={lang.handle('Selecciona un rol')}
                  optionFilterProp="role_uuid"
                  onSelect={(value, event) => onChangeSelect(value, event)}
                  name="role_uuid"
                  value={user.role_uuid}
                  className="register-form__select form-control"
                >
                  {roles.map((option) => (
                    <Option type='role_uuid' key={option.uuid} value={option.uuid}>
                      {option.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
            }
            <div className="form-group row">
              <label
                htmlFor="timezone"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {lang.handle('Zona horaria')}
              </label>
              <div className="col-sm-10">
                <Select
                  showSearch
                  style={{ width: 200 }}
                  placeholder={lang.handle('Seleccione una zona horaria')}
                  onSelect={onChangeSelect}
                  name="timezone"
                  value={user.timezone}
                  className="register-form__select form-control"
                >
                  {timezones.map((option) => (
                    <Option type='timezone' key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
            {editPrivilege && <div className="form-group row">
              <label
                htmlFor="active"
                className="col-sm-2 col-form-label newCard-form-text"
              >
                {" "}
                {" "}
              </label>
              <div className="col-sm-10">
                <Checkbox
                  name="active"
                  checked={user.active}
                  onClick={onChangeCheck}>
                  {lang.handle('Activo')}
                </Checkbox>
              </div>
            </div>
            }
          {/* Cierra la sección de datos personales: Guardar solo afecta al formulario */}
          <div className="button-edit-user user-form-footer">
            <Button
              id="button-save"
              htmlType="submit"
              type="primary"
              icon={<SaveOutlined />}
              onClick={onSubmit}
            >
              {lang.handle('Guardar')}
            </Button>
          </div>

          {!user.isNew &&
            <div className="user-security-section">
              <h6 className="user-section-title">
                {lang.handle('Seguridad y acceso')}
              </h6>
              <Space wrap>
                {  (hasPrivilege('users_edit') || ident === uuid) && <Button
                    icon={<KeyOutlined/>}
                    onClick={changePassword}
                  >
                    {lang.handle('Cambiar contraseña')}
                  </Button>
                }
                {  (hasPrivilege('users_edit') || ident === uuid) && <Button
                    icon={<ApiOutlined/>}
                    onClick={changeCloudApiKey}
                  >
                    {lang.handle('Cambiar clave API Cloud')}
                  </Button>
                }
                {  ident === uuid && <Button
                    icon={<SafetyOutlined/>}
                    onClick={manageTwoFactor}
                  >
                    {lang.handle('Verificación en dos pasos')}
                  </Button>
                }
              </Space>
            </div>
          }
          </form>
        </div>
      </div>
    }

    const apiKeyTab = {
      label: lang.handle('Claves API'), key: '2', children: <div>
        <ApiKeyList user_uuid={ident}/>
      </div>
    }

    const items = [userTab, apiKeyTab];

    return (
      <div className="bg-white card">
        {loading && <Loading loading={loading} />}
        {/* Titulo de la tarjeta */}
        <h4 className="card-title p-3"> {title} </h4>
        {user.isNew ? (
          <div className="p-3">
            {userTab.children}
          </div>
        ) : (
          <Tabs
            type="card"
            items={items}
            className="p-3"
          />
        )}
      </div>
    );
  }

export default withLocale(withNavigation(UserCard));
